export interface SlotDefinition {
  name: string;
  start_time: string;
  end_time: string;
  type: 'lesson' | 'break';
  order_index: number;
}

export interface TimetableConfig {
  startTime: string;
  periodDuration: number;
  periodsBeforeBreak: number;
  breakDuration: number;
  periodsAfterBreak: number;
  lunchAfterPeriod?: number;
  lunchDuration?: number;
}

export interface TimetablePreset {
  id: string;
  name: string;
  description: string;
  schoolType: 'primary' | 'secondary' | 'tertiary' | 'any';
  icon: string;
  activeDays: string[];
  config: TimetableConfig;
}

function addMinutes(time: string, minutes: number): string {
  const [h, m] = time.split(':').map(Number);
  const total = h * 60 + m + minutes;
  const newH = Math.floor(total / 60).toString().padStart(2, '0');
  const newM = (total % 60).toString().padStart(2, '0');
  return `${newH}:${newM}`;
}

export function generateSlotsFromConfig(config: TimetableConfig): SlotDefinition[] {
  const slots: SlotDefinition[] = [];
  let currentTime = config.startTime;
  let periodCount = 0;
  let orderIndex = 1;

  for (let i = 0; i < config.periodsBeforeBreak; i++) {
    periodCount++;
    const endTime = addMinutes(currentTime, config.periodDuration);
    slots.push({
      name: `Period ${periodCount}`,
      start_time: `${currentTime}:00`,
      end_time: `${endTime}:00`,
      type: 'lesson',
      order_index: orderIndex++,
    });
    currentTime = endTime;
  }

  const breakEnd = addMinutes(currentTime, config.breakDuration);
  slots.push({
    name: 'Break',
    start_time: `${currentTime}:00`,
    end_time: `${breakEnd}:00`,
    type: 'break',
    order_index: orderIndex++,
  });
  currentTime = breakEnd;

  for (let i = 0; i < config.periodsAfterBreak; i++) {
    periodCount++;

    if (config.lunchAfterPeriod && periodCount === config.lunchAfterPeriod + 1) {
      const lunchEnd = addMinutes(currentTime, config.lunchDuration || 30);
      slots.push({
        name: 'Lunch Break',
        start_time: `${currentTime}:00`,
        end_time: `${lunchEnd}:00`,
        type: 'break',
        order_index: orderIndex++,
      });
      currentTime = lunchEnd;
    }

    const endTime = addMinutes(currentTime, config.periodDuration);
    slots.push({
      name: `Period ${periodCount}`,
      start_time: `${currentTime}:00`,
      end_time: `${endTime}:00`,
      type: 'lesson',
      order_index: orderIndex++,
    });
    currentTime = endTime;
  }

  return slots;
}

export function getEndTime(config: TimetableConfig): string {
  const slots = generateSlotsFromConfig(config);
  return slots[slots.length - 1]?.end_time || '';
}

export function formatDisplayTime(time: string): string {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours || '0', 10);
  const m = minutes || '00';
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${m} ${ampm}`;
}

export const ALL_DAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'] as const;

export const WEEKDAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'] as const;

export const PERIOD_DURATION_OPTIONS = [
  { label: '30 min', value: 30, hint: 'Common for nursery/KG' },
  { label: '40 min', value: 40, hint: 'Standard for primary & secondary' },
  { label: '45 min', value: 45, hint: 'Extended secondary period' },
  { label: '60 min', value: 60, hint: 'University lecture block' },
  { label: '90 min', value: 90, hint: 'Double lecture period' },
];

export const SCHOOL_TYPE_OPTIONS = [
  { label: 'Nursery / KG', value: 'nursery', icon: 'pi-heart' },
  { label: 'Primary School', value: 'primary', icon: 'pi-book' },
  { label: 'Secondary School', value: 'secondary', icon: 'pi-graduation-cap' },
  { label: 'University / Polytechnic', value: 'tertiary', icon: 'pi-building' },
];

export const TIMETABLE_PRESETS: TimetablePreset[] = [
  {
    id: 'nursery-kg',
    name: 'Nursery / KG',
    description: '6 periods x 30 min, 1 break',
    schoolType: 'primary',
    icon: 'pi-heart',
    activeDays: [...WEEKDAYS],
    config: {
      startTime: '08:00',
      periodDuration: 30,
      periodsBeforeBreak: 3,
      breakDuration: 30,
      periodsAfterBreak: 3,
    },
  },
  {
    id: 'standard-primary',
    name: 'Standard Primary',
    description: '7 periods x 40 min, 1 break',
    schoolType: 'primary',
    icon: 'pi-book',
    activeDays: [...WEEKDAYS],
    config: {
      startTime: '08:00',
      periodDuration: 40,
      periodsBeforeBreak: 3,
      breakDuration: 20,
      periodsAfterBreak: 4,
    },
  },
  {
    id: 'standard-secondary',
    name: 'Standard Secondary',
    description: '8 periods x 40 min, break + lunch',
    schoolType: 'secondary',
    icon: 'pi-graduation-cap',
    activeDays: [...WEEKDAYS],
    config: {
      startTime: '08:00',
      periodDuration: 40,
      periodsBeforeBreak: 3,
      breakDuration: 15,
      periodsAfterBreak: 5,
      lunchAfterPeriod: 5,
      lunchDuration: 30,
    },
  },
  {
    id: 'secondary-saturday',
    name: 'Secondary + Saturday',
    description: '8 periods x 40 min, Mon-Sat',
    schoolType: 'secondary',
    icon: 'pi-calendar-plus',
    activeDays: [...WEEKDAYS, 'SATURDAY'],
    config: {
      startTime: '08:00',
      periodDuration: 40,
      periodsBeforeBreak: 3,
      breakDuration: 15,
      periodsAfterBreak: 5,
      lunchAfterPeriod: 5,
      lunchDuration: 30,
    },
  },
  {
    id: 'university-lecture',
    name: 'University Lecture',
    description: '7 x 1hr blocks, break + lunch',
    schoolType: 'tertiary',
    icon: 'pi-building',
    activeDays: [...WEEKDAYS],
    config: {
      startTime: '08:00',
      periodDuration: 60,
      periodsBeforeBreak: 3,
      breakDuration: 15,
      periodsAfterBreak: 4,
      lunchAfterPeriod: 4,
      lunchDuration: 60,
    },
  },
  {
    id: 'half-day',
    name: 'Half-Day Schedule',
    description: '4 periods x 45 min, 1 break',
    schoolType: 'any',
    icon: 'pi-clock',
    activeDays: [...WEEKDAYS],
    config: {
      startTime: '08:00',
      periodDuration: 45,
      periodsBeforeBreak: 2,
      breakDuration: 15,
      periodsAfterBreak: 2,
    },
  },
];
